import React from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, AlertCircle, Shield, Sparkles } from "lucide-react";

const SenderHeader = ({ senderIds }) => {
  const senderIdsArray = Array.isArray(senderIds) ? senderIds : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative mb-8 sm:mb-12"
    >
      {/* Sophisticated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-gray-200/30 to-transparent rounded-full blur-2xl"></div>
        <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-slate-300/20 to-transparent rounded-full blur-xl"></div>
      </div>

      <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden">
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-50/50 to-transparent"></div>

        <div className="relative p-4 sm:p-6 lg:p-12">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8">
            {/* Left Content */}
            <div className="flex-1">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="relative">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-gray-700 via-slate-700 to-gray-800 rounded-2xl flex items-center justify-center shadow-lg">
                    <Send className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                  </div>
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                      Sender ID Management
                    </h1>
                    <div className="hidden sm:flex items-center gap-1">
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                      <div className="w-1.5 h-1.5 bg-gray-200 rounded-full"></div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs sm:text-sm lg:text-base">
                    Professional SMS identity management
                  </p>
                </div>
              </div>

              <p className="text-gray-600 leading-relaxed mb-4 sm:mb-6 max-w-2xl text-sm sm:text-base">
                Create, manage, and optimize your sender identities to build
                trust and enhance the effectiveness of your SMS campaigns with
                professional branding.
              </p>

              {/* Feature Pills */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-red-50 text-red-700 rounded-full text-xs sm:text-sm font-medium border border-red-200">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span>Instant Validation</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-amber-50 text-amber-700 rounded-full text-xs sm:text-sm font-medium border border-amber-200">
                  <Shield className="w-3 h-3" />
                  <span>Secure & Compliant</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-50 text-gray-700 rounded-full text-xs sm:text-sm font-medium border border-gray-200">
                  <Sparkles className="w-3 h-3" />
                  <span>Easy Management</span>
                </div>
              </div>
            </div>

            {/* Right Visual Element */}
            <div className="lg:flex-shrink-0">
              <div className="relative">
                {/* Main Circle */}
                <div className="w-28 h-28 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center shadow-inner mx-auto lg:mx-0">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-gray-700 to-slate-800 rounded-full flex items-center justify-center shadow-lg">
                    <div className="text-white font-bold text-base sm:text-lg lg:text-xl">
                      {senderIdsArray.length}
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg"
                >
                  <CheckCircle className="w-4 h-4 text-white" />
                </motion.div>

                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.5,
                  }}
                  className="absolute -bottom-2 -left-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center shadow-lg"
                >
                  <AlertCircle className="w-3 h-3 text-white" />
                </motion.div>

                {/* Stats Ring - Desktop only animation for better performance */}
                <div className="hidden lg:block absolute inset-0 rounded-full border-2 border-dashed border-gray-300/70 animate-spin-slow"></div>
                {/* Mobile/Tablet-friendly subtle static ring */}
                <div className="lg:hidden absolute inset-0 rounded-full border border-gray-200/40"></div>
              </div>

              {/* Quick Stats */}
              <div className="mt-6 grid grid-cols-2 gap-4 text-center lg:text-left">
                <div className="bg-white/70 rounded-xl p-3 border border-gray-200/50">
                  <div className="text-lg font-bold text-red-600">
                    {
                      senderIdsArray.filter(
                        (s) => s.status && s.status.toLowerCase() === "approved"
                      ).length
                    }
                  </div>
                  <div className="text-xs text-gray-600">Approved</div>
                </div>
                <div className="bg-white/70 rounded-xl p-3 border border-gray-200/50">
                  <div className="text-lg font-bold text-amber-600">
                    {
                      senderIdsArray.filter(
                        (s) => s.status && s.status.toLowerCase() === "pending"
                      ).length
                    }
                  </div>
                  <div className="text-xs text-gray-600">Pending</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SenderHeader;
